import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { CapturePanel } from "../components/CapturePanel";
import { FocusDreaming } from "../components/FocusDreaming";
import { GrowPanel, GROW_TABS } from "../components/GrowPanel";
import type { GrowTab } from "../components/GrowPanel";
import { LibraryArea } from "../components/LibraryArea";
import { LoginScreen } from "../components/LoginScreen";
import { BookMapPanel } from "../components/BookMapPanel";
import { ReadPanel } from "../components/ReadPanel";
import { SourceTree } from "../components/SourceTree";
import { useToast } from "../components/ToastProvider";
import { UserGuidePanel } from "../components/UserGuidePanel";
import type { AppTab } from "../components/AppShell";
import {
  type AttachmentUploadOptions,
  captureNote,
  archiveSource,
  createLink,
  createOntologyType,
  createReadAnchor,
  createOutput,
  createRelation,
  createRelationType,
  createSource,
  deleteAttachment,
  deleteLink,
  deleteRelation,
  deleteReadAnchor,
  listFocusSessions,
  loadBootstrap,
  loadAttachments,
  loadCandidates,
  loadDueReviews,
  loadGardenStats,
  markReviewed,
  loadReadAnchors,
  loadLinks,
  loadSession,
  loadSourceNotes,
  loginWithPasscode,
  loginWithCredentials,
  registerAccount,
  logoutSession,
  reviewCandidate,
  resolveReadUrl,
  setObjectVisibility,
  searchObjects,
  suggestForNote,
  transcribeAttachment,
  updateSource,
  updateNote,
  updateOutput,
  updateObjectStatus,
  updateObjectTags,
  updateOntologyType,
  updateRelation,
  updateRelationType,
  uploadAttachment
} from "../lib/api";
import type {
  Attachment,
  DueReview,
  EntityLink,
  ExtractionCandidate,
  FocusSession,
  GardenMetric,
  GardenStats,
  GraphLink,
  NoteCard,
  OntologyType,
  ReadAnchor,
  ReadUrlResolution,
  RelationType,
  SearchResult,
  SourceKind,
  SourceNode
} from "../types";

type Bootstrap = {
  sourceNodes: SourceNode[];
  notes: NoteCard[];
  gardenMetrics: GardenMetric[];
  graphLinks: GraphLink[];
  candidates: ExtractionCandidate[];
  ontologyTypes: OntologyType[];
  relationTypes: RelationType[];
  mode?: string;
};

function sourceSubtreeIds(sourceId: string, sources: SourceNode[]) {
  const ids = new Set<string>([sourceId]);
  let changed = true;

  while (changed) {
    changed = false;
    for (const source of sources) {
      if (source.parentId && ids.has(source.parentId) && !ids.has(source.id)) {
        ids.add(source.id);
        changed = true;
      }
    }
  }

  return ids;
}

export function Home() {
  const { notify } = useToast();
  // Start empty (new accounts have no data); bootstrap fills it after auth.
  const [data, setData] = useState<Bootstrap>({
    sourceNodes: [],
    notes: [],
    gardenMetrics: [],
    graphLinks: [],
    candidates: [],
    ontologyTypes: [],
    relationTypes: [],
    mode: "initial"
  });
  const [selectedId, setSelectedId] = useState("");
  const [loadingNotesSourceId, setLoadingNotesSourceId] = useState<string | null>(null);
  const [sourceNotesError, setSourceNotesError] = useState("");
  const [captureError, setCaptureError] = useState("");
  const [sourceError, setSourceError] = useState("");
  const [relationError, setRelationError] = useState("");
  const [aiError, setAiError] = useState("");
  const [attachmentError, setAttachmentError] = useState("");
  const [transcribeError, setTranscribeError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [readError, setReadError] = useState("");
  const [ontologyError, setOntologyError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [attachmentsByObject, setAttachmentsByObject] = useState<Record<string, Attachment[]>>({});
  const [linksByObject, setLinksByObject] = useState<Record<string, EntityLink[]>>({});
  const [anchorsBySource, setAnchorsBySource] = useState<Record<string, ReadAnchor[]>>({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginError, setLoginError] = useState("");
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<AppTab>(() => {
    const tab = searchParams.get("tab");
    return tab === "library" || tab === "grow" ? tab : "capture";
  });
  const navigate = useNavigate();
  const [focusSessions, setFocusSessions] = useState<FocusSession[]>([]);
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusError, setFocusError] = useState("");
  const [gardenStats, setGardenStats] = useState<GardenStats | null>(null);
  const [gardenLoading, setGardenLoading] = useState(false);
  const [reviews, setReviews] = useState<DueReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [growTab, setGrowTab] = useState<GrowTab>("garden");
  const [workspaceOverlay, setWorkspaceOverlay] = useState<{ mode: "link" | "book"; sourceId: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    loadSession()
      .then(async (session) => {
        if (!isMounted) {
          return;
        }

        setIsAuthenticated(session.authenticated);

        if (session.authenticated) {
          const payload = await loadBootstrap();
          const candidatesPayload = await loadCandidates();
          if (!isMounted) {
            return;
          }
          setData({
            ...payload,
            candidates: candidatesPayload.data ?? [],
            ontologyTypes: payload.ontologyTypes ?? [],
            relationTypes: payload.relationTypes ?? []
          });
          setSelectedId("");
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsCheckingAuth(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedSource = useMemo(
    () => data.sourceNodes.find((node) => node.id === selectedId),
    [data.sourceNodes, selectedId]
  );

  const refreshData = async () => {
    const [payload, candidatesPayload] = await Promise.all([loadBootstrap(), loadCandidates()]);
    setData((current) => ({
      ...payload,
      notes: payload.notes.length > 0 ? payload.notes : current.notes,
      candidates: candidatesPayload.data ?? [],
      ontologyTypes: payload.ontologyTypes ?? [],
      relationTypes: payload.relationTypes ?? []
    }));
    return payload;
  };

  const loadNotesForSource = useCallback(async (sourceId: string) => {
    if (!sourceId) {
      return;
    }

    setSourceNotesError("");
    setLoadingNotesSourceId(sourceId);

    try {
      const payload = await loadSourceNotes(sourceId);
      setData((current) => {
        const scopedSourceIds = sourceSubtreeIds(sourceId, current.sourceNodes);
        return {
          ...current,
          notes: [
            ...(payload.data ?? []),
            ...current.notes.filter((note) => !scopedSourceIds.has(note.sourceId))
          ]
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Load source notes failed";
      setSourceNotesError(message);
    } finally {
      setLoadingNotesSourceId((current) => (current === sourceId ? null : current));
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !selectedId) {
      return;
    }

    void loadNotesForSource(selectedId).catch(() => {
      // The inline source detail panel surfaces the error state.
    });
  }, [isAuthenticated, loadNotesForSource, selectedId]);

  const handleCapture = async (input: {
    kind: NoteCard["kind"];
    content: string;
    sourceId: string;
    relationType?: string;
    relationTargetId?: string;
    status?: string;
    tags?: string[];
    properties?: Record<string, unknown>;
  }) => {
    setCaptureError("");

    try {
      const result = await captureNote(input);
      await refreshData();
      setSelectedId(input.sourceId);
      await loadNotesForSource(input.sourceId);
      notify({ type: "success", title: "Đã lưu note", description: "+1 điểm khu vườn học tập." });
      return result.data as { objectId?: string } | undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Capture failed";
      setCaptureError(message);
      notify({ type: "error", title: "Không lưu được note", description: message });
      throw error;
    }
  };

  const handleUpdateNote = async (id: string, input: { content: string }) => {
    setCaptureError("");

    try {
      await updateNote(id, input);
      await refreshData();
      if (selectedId) {
        await loadNotesForSource(selectedId);
      }
      notify({ type: "success", title: "Da cap nhat note" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update note failed";
      setCaptureError(message);
      notify({ type: "error", title: "Khong cap nhat duoc note", description: message });
      throw error;
    }
  };

  const handleCreateSource = async (input: { title: string; kind: SourceKind; parentId?: string; status?: string; tags?: string[]; properties?: Record<string, unknown> }) => {
    setSourceError("");

    try {
      const result = await createSource(input);
      const refreshed = await refreshData();
      const createdId = result.data?.sourceId ?? result.data?.id;
      setSelectedId(createdId ?? input.parentId ?? refreshed.sourceNodes[0]?.id ?? "");
      notify({ type: "success", title: "Đã tạo nguồn học", description: input.title });
      return createdId as string | undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Create source failed";
      setSourceError(message);
      notify({ type: "error", title: "Không tạo được nguồn", description: message });
      throw error;
    }
  };

  const handleCreateOutput = async (input: {
    title: string;
    content: string;
    inputObjectIds: string[];
    parentSourceId?: string;
    tags?: string[];
    properties?: Record<string, unknown>;
  }) => {
    setSourceError("");
    setRelationError("");

    try {
      const result = await createOutput(input);
      const outputId = result.data.outputId;
      await refreshData();
      setSelectedId(outputId);
      await loadNotesForSource(outputId);
      notify({ type: "success", title: "Đã tạo output", description: input.title });
      return outputId;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Create output failed";
      setSourceError(message);
      notify({ type: "error", title: "Không tạo được output", description: message });
      throw error;
    }
  };

  const handleUpdateOutput = async (id: string, input: { title: string; content: string }) => {
    setSourceError("");

    try {
      await updateOutput(id, input);
      await refreshData();
      setSelectedId(id);
      notify({ type: "success", title: "Đã cập nhật output", description: input.title });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update output failed";
      setSourceError(message);
      notify({ type: "error", title: "Không cập nhật được output", description: message });
      throw error;
    }
  };

  const handleUpdateSource = async (
    id: string,
    input: { title?: string; kind?: SourceKind; progress?: number; parentId?: string | null; status?: string; properties?: Record<string, unknown> }
  ) => {
    setSourceError("");

    try {
      await updateSource(id, input);
      await refreshData();
      if (selectedId) {
        await loadNotesForSource(selectedId);
      }
      notify({ type: "success", title: "Đã cập nhật nguồn" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update source failed";
      setSourceError(message);
      notify({ type: "error", title: "Không cập nhật được nguồn", description: message });
      throw error;
    }
  };

  // Change lifecycle/status of any node (note or source child) inline from the library.
  const handleUpdateNodeStatus = async (id: string, status: string) => {
    setSourceError("");

    try {
      await updateObjectStatus(id, status);
      await refreshData();
      if (selectedId) {
        await loadNotesForSource(selectedId);
      }
      notify({ type: "success", title: "Đã đổi trạng thái" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update status failed";
      setSourceError(message);
      notify({ type: "error", title: "Không đổi được trạng thái", description: message });
      throw error;
    }
  };

  // Change tags (categories) of any node inline from the library.
  const handleUpdateNodeTags = async (id: string, tags: string[]) => {
    setSourceError("");

    try {
      await updateObjectTags(id, tags);
      await refreshData();
      if (selectedId) {
        await loadNotesForSource(selectedId);
      }
      notify({ type: "success", title: "Đã cập nhật tag" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update tags failed";
      setSourceError(message);
      notify({ type: "error", title: "Không cập nhật được tag", description: message });
      throw error;
    }
  };

  const handleArchiveSource = async (id: string) => {
    setSourceError("");

    try {
      await archiveSource(id);
      await refreshData();
      setSelectedId("");
      notify({ type: "success", title: "Đã lưu trữ nguồn" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Archive source failed";
      setSourceError(message);
      notify({ type: "error", title: "Không lưu trữ được nguồn", description: message });
      throw error;
    }
  };

  const handleCreateRelation = async (input: {
    sourceObjectId: string;
    relationType: string;
    targetObjectId: string;
    properties?: Record<string, unknown>;
  }) => {
    setRelationError("");

    try {
      const result = await createRelation(input);
      await refreshData();
      if (selectedId) {
        await loadNotesForSource(selectedId);
      }
      notify({ type: "success", title: result.mode === "existing" ? "Kết nối đã tồn tại" : "Đã tạo kết nối" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Create relation failed";
      setRelationError(message);
      notify({ type: "error", title: "Không tạo được kết nối", description: message });
      throw error;
    }
  };

  const handleUpdateRelation = async (id: string, input: { relationType: string }) => {
    setRelationError("");

    try {
      const result = await updateRelation(id, input);
      await refreshData();
      if (selectedId) {
        await loadNotesForSource(selectedId);
      }
      notify({ type: "success", title: result.mode === "existing" ? "Kết nối đã tồn tại" : "Đã cập nhật kết nối" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update relation failed";
      setRelationError(message);
      notify({ type: "error", title: "Không cập nhật được kết nối", description: message });
      throw error;
    }
  };

  const handleDeleteRelation = async (id: string) => {
    setRelationError("");

    try {
      await deleteRelation(id);
      await refreshData();
      if (selectedId) {
        await loadNotesForSource(selectedId);
      }
      notify({ type: "success", title: "Đã xóa kết nối" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Delete relation failed";
      setRelationError(message);
      notify({ type: "error", title: "Không xóa được kết nối", description: message });
      throw error;
    }
  };

  const completeAuth = async () => {
    await refreshData();
    setSelectedId("");
    setIsAuthenticated(true);
  };

  const handleLoginCredentials = async (input: { email: string; password: string }) => {
    setLoginError("");
    try {
      await loginWithCredentials(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng nhập thất bại");
      throw error;
    }
  };

  const handleRegister = async (input: { email: string; username: string; password: string }) => {
    setLoginError("");
    try {
      await registerAccount(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng ký thất bại");
      throw error;
    }
  };

  const handleLoginPasscode = async (passcode: string) => {
    setLoginError("");
    try {
      await loginWithPasscode(passcode);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Login failed");
      throw error;
    }
  };

  const handleLogout = async () => {
    await logoutSession();
    setIsAuthenticated(false);
  };

  const handleSetVisibility = async (objectId: string, visibility: "public" | "private") => {
    try {
      await setObjectVisibility(objectId, visibility);
      await refreshData();
      notify({ type: "success", title: visibility === "public" ? "Đã công khai" : "Đã chuyển riêng tư" });
    } catch (error) {
      notify({ type: "error", title: "Không đổi được hiển thị", description: error instanceof Error ? error.message : "Visibility update failed" });
      throw error;
    }
  };

  const refreshFocusSessions = async () => {
    setFocusError("");
    setFocusLoading(true);
    try {
      const payload = await listFocusSessions();
      setFocusSessions(payload.data ?? []);
    } catch (error) {
      setFocusError(error instanceof Error ? error.message : "Không tải được lịch sử phiên.");
    } finally {
      setFocusLoading(false);
    }
  };

  const refreshGardenStats = async () => {
    setGardenLoading(true);
    try {
      setGardenStats(await loadGardenStats());
    } catch {
      // ambient feature — leave the last value on error
    } finally {
      setGardenLoading(false);
    }
  };

  const refreshReviews = async () => {
    setReviewsLoading(true);
    try {
      const payload = await loadDueReviews();
      setReviews(payload.data ?? []);
    } catch {
      // best-effort
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewed = async (objectId: string) => {
    await markReviewed(objectId);
    setReviews((current) => current.filter((item) => item.id !== objectId));
    void refreshGardenStats();
    notify({ type: "success", title: "Đã đánh dấu ôn tập" });
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === "grow") {
      void refreshFocusSessions();
      void refreshGardenStats();
      void refreshReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab]);

  const handleSuggest = async (objectId: string) => {
    setAiError("");

    try {
      const candidatesPayload = await suggestForNote(objectId);
      setData((current) => ({ ...current, candidates: candidatesPayload.data ?? [] }));
      notify({ type: "success", title: "Đã tạo gợi ý AI" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Suggest failed";
      setAiError(message);
      notify({ type: "error", title: "Không tạo được gợi ý", description: message });
      throw error;
    }
  };

  const handleReviewCandidate = async (id: string, decision: "accepted" | "rejected") => {
    setAiError("");

    try {
      const candidatesPayload = await reviewCandidate(id, decision);
      setData((current) => ({ ...current, candidates: candidatesPayload.data ?? [] }));
      notify({ type: "success", title: decision === "accepted" ? "Đã chấp nhận gợi ý" : "Đã bỏ gợi ý" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Review candidate failed";
      setAiError(message);
      notify({ type: "error", title: "Không xử lý được gợi ý", description: message });
      throw error;
    }
  };

  const handleLoadAttachments = async (objectId: string) => {
    setAttachmentError("");

    try {
      const payload = await loadAttachments(objectId);
      setAttachmentsByObject((current) => ({ ...current, [objectId]: payload.data ?? [] }));
    } catch (error) {
      setAttachmentError(error instanceof Error ? error.message : "Load attachments failed");
      throw error;
    }
  };

  const handleUploadAttachment = async (objectId: string, file: File, options?: AttachmentUploadOptions) => {
    setAttachmentError("");

    try {
      await uploadAttachment(objectId, file, options);
      await handleLoadAttachments(objectId);
      await refreshData();
      notify({ type: "success", title: "Đã tải file lên", description: file.name });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload attachment failed";
      setAttachmentError(message);
      notify({ type: "error", title: "Không tải được file", description: message });
      throw error;
    }
  };

  const handleTranscribeAttachment = async (noteId: string, attachmentId: string) => {
    setTranscribeError("");

    try {
      const result = await transcribeAttachment(attachmentId);
      if (!result.data || result.error) {
        setTranscribeError("Không chuyển được giọng nói thành chữ. Thử lại.");
        return;
      }
      await refreshData();
      await handleLoadAttachments(noteId);
      notify({ type: "success", title: "Đã chuyển giọng nói thành chữ" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Transcribe failed";
      setTranscribeError(message);
      notify({ type: "error", title: "Không transcript được", description: message });
    }
  };

  const handleDeleteAttachment = async (objectId: string, attachmentId: string) => {
    setAttachmentError("");

    try {
      await deleteAttachment(attachmentId);
      await handleLoadAttachments(objectId);
      await refreshData();
      notify({ type: "success", title: "Đã xóa file" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Delete attachment failed";
      setAttachmentError(message);
      notify({ type: "error", title: "Không xóa được file", description: message });
      throw error;
    }
  };

  const handleLoadLinks = async (objectId: string) => {
    setLinkError("");

    try {
      const payload = await loadLinks(objectId);
      setLinksByObject((current) => ({ ...current, [objectId]: payload.data ?? [] }));
    } catch (error) {
      setLinkError(error instanceof Error ? error.message : "Load links failed");
      throw error;
    }
  };

  const handleCreateLink = async (objectId: string, input: { url: string; label?: string }) => {
    setLinkError("");

    try {
      await createLink({ objectId, ...input });
      await handleLoadLinks(objectId);
      notify({ type: "success", title: "Đã lưu link" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Create link failed";
      setLinkError(message);
      notify({ type: "error", title: "Không lưu được link", description: message });
      throw error;
    }
  };

  const handleDeleteLink = async (objectId: string, linkId: string) => {
    setLinkError("");

    try {
      await deleteLink(linkId);
      await handleLoadLinks(objectId);
      notify({ type: "success", title: "Đã xóa link" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Delete link failed";
      setLinkError(message);
      notify({ type: "error", title: "Không xóa được link", description: message });
      throw error;
    }
  };

  const handleLoadReadAnchors = async (sourceId: string) => {
    setReadError("");

    try {
      const payload = await loadReadAnchors(sourceId);
      setAnchorsBySource((current) => ({ ...current, [sourceId]: payload.data ?? [] }));
    } catch (error) {
      setReadError(error instanceof Error ? error.message : "Load read anchors failed");
      throw error;
    }
  };

  const handleResolveReadUrl = async (url: string) => {
    setReadError("");

    try {
      const payload = await resolveReadUrl(url);
      return payload.data as ReadUrlResolution;
    } catch (error) {
      setReadError(error instanceof Error ? error.message : "Resolve read URL failed");
      throw error;
    }
  };

  const handleCreateReadAnchor = async (
    sourceId: string,
    input: {
      sourceUrl?: string;
      quoteText: string;
      prefix?: string;
      suffix?: string;
      noteTitle?: string;
      noteContent?: string;
    }
  ) => {
    setReadError("");

    try {
      const payload = await createReadAnchor(sourceId, input);
      setAnchorsBySource((current) => ({ ...current, [sourceId]: payload.data ?? [] }));
      await refreshData();
      notify({ type: "success", title: "Đã lưu trích đoạn" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Create read anchor failed";
      setReadError(message);
      notify({ type: "error", title: "Không lưu được trích đoạn", description: message });
      throw error;
    }
  };

  const handleDeleteReadAnchor = async (sourceId: string, anchorId: string) => {
    setReadError("");

    try {
      await deleteReadAnchor(anchorId);
      await handleLoadReadAnchors(sourceId);
      notify({ type: "success", title: "Đã xóa trích đoạn" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Delete read anchor failed";
      setReadError(message);
      notify({ type: "error", title: "Không xóa được trích đoạn", description: message });
      throw error;
    }
  };

  const syncOntologyPayload = (payload: { data?: { types?: OntologyType[]; relationTypes?: RelationType[] } }) => {
    setData((current) => ({
      ...current,
      ontologyTypes: payload.data?.types ?? current.ontologyTypes,
      relationTypes: payload.data?.relationTypes ?? current.relationTypes
    }));
  };

  const handleCreateOntologyType = async (input: { slug: string; name: string; description?: string; parentTypeId?: string | null }) => {
    setOntologyError("");

    try {
      syncOntologyPayload(await createOntologyType(input));
      notify({ type: "success", title: "Đã tạo loại thực thể" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Create entity type failed";
      setOntologyError(message);
      notify({ type: "error", title: "Không tạo được loại thực thể", description: message });
      throw error;
    }
  };

  const handleUpdateOntologyType = async (id: string, input: { name?: string; description?: string; parentTypeId?: string | null }) => {
    setOntologyError("");

    try {
      syncOntologyPayload(await updateOntologyType(id, input));
      notify({ type: "success", title: "Đã cập nhật loại thực thể" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update entity type failed";
      setOntologyError(message);
      notify({ type: "error", title: "Không cập nhật được loại thực thể", description: message });
      throw error;
    }
  };

  const handleCreateRelationType = async (input: {
    slug: string;
    name: string;
    nameVi?: string;
    description?: string;
    descriptionVi?: string;
    sourceTypeId?: string | null;
    targetTypeId?: string | null;
    inverseSlug?: string | null;
    directional?: boolean;
  }) => {
    setOntologyError("");

    try {
      syncOntologyPayload(await createRelationType(input));
      notify({ type: "success", title: "Đã tạo loại kết nối" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Create relation type failed";
      setOntologyError(message);
      notify({ type: "error", title: "Không tạo được loại kết nối", description: message });
      throw error;
    }
  };

  const handleUpdateRelationType = async (
    id: string,
    input: {
      name?: string;
      description?: string;
      sourceTypeId?: string | null;
      targetTypeId?: string | null;
      inverseSlug?: string | null;
      directional?: boolean;
    }
  ) => {
    setOntologyError("");

    try {
      syncOntologyPayload(await updateRelationType(id, input));
      notify({ type: "success", title: "Đã cập nhật loại kết nối" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update relation type failed";
      setOntologyError(message);
      notify({ type: "error", title: "Không cập nhật được loại kết nối", description: message });
      throw error;
    }
  };

  const handleSearch = (query: string) => {
    const normalized = query.trim();
    setSearchError("");

    if (!normalized) {
      setSearchResults([]);
      return;
    }

    window.setTimeout(() => {
      searchObjects(normalized)
        .then((payload) => setSearchResults(payload.data ?? []))
        .catch((error) => setSearchError(error instanceof Error ? error.message : "Search failed"));
    }, 150);
  };

  const openSourceWorkspace = (source: { id: string; kind: SourceKind; properties?: Record<string, unknown> }) => {
    setSelectedId(source.id);
    setWorkspaceOverlay({
      sourceId: source.id,
      mode: ["article", "youtube", "output"].includes(source.kind) || typeof source.properties?.sourceUrl === "string" ? "link" : "book"
    });
  };

  const handleSelectSource = (sourceId: string) => {
    if (sourceId === selectedId) {
      void loadNotesForSource(sourceId).catch(() => {
        // The inline source detail panel surfaces the error state.
      });
      return;
    }

    setSelectedId(sourceId);
  };

  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-sm font-bold text-muted">
        Loading Human Note
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <LoginScreen
        onLoginCredentials={handleLoginCredentials}
        onRegister={handleRegister}
        onLoginPasscode={handleLoginPasscode}
        error={loginError}
      />
    );
  }

  const mainPanel =
    activeTab === "library" ? (
      <LibraryArea
        selectedSource={selectedSource}
        sources={data.sourceNodes}
        notes={data.notes}
        notesLoading={Boolean(selectedId && loadingNotesSourceId === selectedId)}
        notesError={sourceNotesError}
        graphLinks={data.graphLinks}
        gardenMetrics={data.gardenMetrics}
        relationTypes={data.relationTypes}
        relationError={relationError}
        onOpenWorkspace={openSourceWorkspace}
        onOpenCapture={() => setActiveTab("capture")}
        onCreateOutput={handleCreateOutput}
        onUpdateOutput={handleUpdateOutput}
        onCreateRelation={handleCreateRelation}
        onUpdateRelation={handleUpdateRelation}
        onDeleteRelation={handleDeleteRelation}
        onUpdateSource={handleUpdateSource}
        onUpdateNote={handleUpdateNote}
        onUpdateStatus={handleUpdateNodeStatus}
        onUpdateTags={handleUpdateNodeTags}
      />
    ) : activeTab === "grow" ? (
      <GrowPanel
        tab={growTab}
        sources={data.sourceNodes}
        reviews={reviews}
        reviewsLoading={reviewsLoading}
        onReviewed={handleReviewed}
        gardenStats={gardenStats}
        gardenLoading={gardenLoading}
        focusSessions={focusSessions}
        focusLoading={focusLoading}
        focusError={focusError}
      />
    ) : data.sourceNodes.length === 0 ? (
      <section className="rounded-lg border border-line bg-white px-6 py-12 shadow-panel">
        <div className="mx-auto max-w-xl text-center">
          <h1 className="text-xl font-bold">Bắt đầu vòng học đầu tiên</h1>
          <p className="mt-2 text-sm leading-6 text-muted">
            Human Note hoạt động tốt nhất khi bạn đi theo vòng: tạo nguồn học, ghi note đầu tiên, rồi kết nối hoặc ôn lại.
          </p>
        </div>
        <div className="mx-auto mt-6 grid max-w-2xl gap-3 text-left md:grid-cols-3">
          {["Tạo một nguồn ở cột bên trái", "Ghi note đầu tiên vào nguồn đó", "Mở Guide để biết cách kết nối"].map((item, index) => (
            <div key={item} className="rounded-lg border border-line bg-slate-50 p-3">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 text-xs font-bold text-brand">{index + 1}</span>
              <p className="mt-2 text-sm font-semibold text-slate-700">{item}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <p className="text-sm font-semibold text-brand">Bắt đầu ở cột Nguồn học bên trái</p>
          <button
            className="inline-flex h-9 items-center justify-center rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
            type="button"
            onClick={() => setGuideOpen(true)}
          >
            Mở hướng dẫn
          </button>
        </div>
      </section>
    ) : (
      <CapturePanel
        notes={data.notes}
        selectedSource={selectedSource}
        sources={data.sourceNodes}
        onCapture={handleCapture}
        onUpdateNote={handleUpdateNote}
        onUpdateSource={handleUpdateSource}
        onCreateRelation={handleCreateRelation}
        onCreateRelationType={handleCreateRelationType}
        onSuggest={handleSuggest}
        onReviewCandidate={handleReviewCandidate}
        candidates={data.candidates}
        attachmentsByObject={attachmentsByObject}
        linksByObject={linksByObject}
        searchResults={searchResults}
        relationTypes={data.relationTypes}
        captureError={captureError}
        relationError={relationError}
        aiError={aiError}
        attachmentError={attachmentError}
        transcribeError={transcribeError}
        linkError={linkError}
        searchError={searchError}
        onTranscribeAttachment={handleTranscribeAttachment}
        onSetVisibility={handleSetVisibility}
        onLoadAttachments={handleLoadAttachments}
        onUploadAttachment={handleUploadAttachment}
        onDeleteAttachment={handleDeleteAttachment}
        onLoadLinks={handleLoadLinks}
        onCreateLink={handleCreateLink}
        onDeleteLink={handleDeleteLink}
      />
    );

  const homeMainPanel = mainPanel;

  const focusWidget = (
    <FocusDreaming
      onSessionsChanged={() => {
        void refreshFocusSessions();
        void refreshGardenStats();
      }}
    />
  );

  const sourceTreeRail = (
    <SourceTree
      nodes={data.sourceNodes}
      selectedId={selectedId}
      onSelect={handleSelectSource}
      onCreateSource={handleCreateSource}
      onUpdateSource={handleUpdateSource}
      onArchiveSource={handleArchiveSource}
      onLoadLinks={handleLoadLinks}
      onCreateLink={handleCreateLink}
      onDeleteLink={handleDeleteLink}
      onOpenWorkspace={openSourceWorkspace}
      onSetVisibility={handleSetVisibility}
      linksByObject={linksByObject}
      sourceError={sourceError}
      linkError={linkError}
    />
  );

  const growMenu = (
    <nav className="space-y-1 rounded-lg border border-line bg-white p-2 shadow-panel">
      <p className="px-2 py-1 text-xs font-bold uppercase tracking-wide text-muted">Phát triển</p>
      {GROW_TABS.map((item) => (
        <button
          key={item.value}
          type="button"
          className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold ${
            growTab === item.value ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
          }`}
          onClick={() => setGrowTab(item.value)}
        >
          <item.icon size={15} aria-hidden="true" /> {item.label}
        </button>
      ))}
    </nav>
  );

  const leftRail =
    activeTab === "capture" ? (
      <>
        {focusWidget}
        {sourceTreeRail}
      </>
    ) : activeTab === "library" ? (
      sourceTreeRail
    ) : (
      <>
        {focusWidget}
        {growMenu}
      </>
    );

  const workspacePanel =
    workspaceOverlay?.mode === "link" ? (
      <ReadPanel
        sources={data.sourceNodes}
        notes={data.notes}
        linksByObject={linksByObject}
        anchorsBySource={anchorsBySource}
        ontologyTypes={data.ontologyTypes}
        relationTypes={data.relationTypes}
        initialSourceId={workspaceOverlay.sourceId}
        sourceError={sourceError}
        linkError={linkError}
        readError={readError}
        relationError={relationError}
        ontologyError={ontologyError}
        onCreateSource={handleCreateSource}
        onUpdateSource={async (id, input) => {
          await handleUpdateSource(id, input);
        }}
        onCreateLink={handleCreateLink}
        onDeleteLink={handleDeleteLink}
        onCreateRelation={handleCreateRelation}
        onLoadLinks={handleLoadLinks}
        onResolveUrl={handleResolveReadUrl}
        onLoadAnchors={handleLoadReadAnchors}
        onCreateAnchor={handleCreateReadAnchor}
        onDeleteAnchor={handleDeleteReadAnchor}
        onCreateType={handleCreateOntologyType}
        onUpdateType={handleUpdateOntologyType}
        onCreateRelationType={handleCreateRelationType}
        onUpdateRelationType={handleUpdateRelationType}
        onSelectSource={(id) => {
          setSelectedId(id);
          setWorkspaceOverlay({ mode: "link", sourceId: id });
        }}
      />
    ) : workspaceOverlay?.mode === "book" ? (
      <BookMapPanel
        sources={data.sourceNodes}
        notes={data.notes}
        selectedSourceId={workspaceOverlay.sourceId}
        onSelectSource={(id) => {
          setSelectedId(id);
          setWorkspaceOverlay({ mode: "book", sourceId: id });
        }}
        onCreateSource={handleCreateSource}
        onUpdateSource={handleUpdateSource}
        onArchiveSource={handleArchiveSource}
        onCapture={handleCapture}
        onUpdateNote={handleUpdateNote}
        onCreateLink={handleCreateLink}
        onLoadLinks={handleLoadLinks}
        onDeleteLink={handleDeleteLink}
        linksByObject={linksByObject}
        onCreateRelation={handleCreateRelation}
        sourceError={sourceError}
        captureError={captureError}
        linkError={linkError}
        relationError={relationError}
      />
    ) : null;

  return (
    <>
      <AppShell
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onLogout={handleLogout}
        onSearch={handleSearch}
        onOpenGuide={() => setGuideOpen(true)}
        left={leftRail}
        main={homeMainPanel}
        right={null}
      />
      {guideOpen ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 px-4 py-5">
          <section className="mx-auto max-w-[1180px] rounded-lg border border-line bg-paper shadow-panel">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-white px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-ink">Hướng dẫn nhanh</p>
                <p className="truncate text-xs font-medium text-muted">Tạo nguồn, ghi note, kết nối lại thành vòng học.</p>
              </div>
              <button
                className="inline-flex h-9 items-center justify-center rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
                type="button"
                onClick={() => setGuideOpen(false)}
              >
                Đóng
              </button>
            </div>
            <div className="p-4">
              <UserGuidePanel
                onStartCapture={() => {
                  setGuideOpen(false);
                  setActiveTab("capture");
                }}
              />
            </div>
          </section>
        </div>
      ) : null}
      {workspaceOverlay && workspacePanel ? (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-slate-900/40 px-4 py-5">
          <section className="mx-auto max-w-[1560px] rounded-lg border border-line bg-paper shadow-panel">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-white px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-ink">
                  {workspaceOverlay.mode === "link" ? "Link workspace" : "Book workspace"}
                </p>
                <p className="truncate text-xs font-medium text-muted">Giữ nguyên tab hiện tại, đóng panel để quay lại đúng chỗ đang làm.</p>
              </div>
              <button
                className="inline-flex h-9 items-center justify-center rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
                type="button"
                onClick={() => setWorkspaceOverlay(null)}
              >
                Đóng
              </button>
            </div>
            <div className="p-4">{workspacePanel}</div>
          </section>
        </div>
      ) : null}
    </>
  );
}
